import "./formDropdown.scss";

import _ from "lodash";
import PropTypes from "prop-types";
import React, { Fragment, PureComponent } from "react";
import { PopoverBody } from "reactstrap";

import NavigationToolbar from "../../components/navigationToolbar";
import { withScreenContext } from "../../components/screen/ScreenContext";
import { COLOR_PICKER_POPOVER_WIDTH } from "../../constants";
import KeyConstants from "../../constants/keyConstants";
import { isScreenModeMobile } from "../../constants/screenModes";
import { PropTypeShapes } from "../../types";
import preprocessorQuery from "../../utils/preprocessorQuery";
import uniqueId from "../../utils/uniqueId";
import { withContainerContext } from "../container/ContainerContext";
import Icon from "../interface/Icon";
import { withModalSizeContext } from "../modal/ModalSizeContext";
import { popoverOrPortal } from "../portal/popoverOrPortal";
import { getDropdownOptions } from "./utils";

const COLORS_PER_ROW = 4;
const CUSTOM_ITEM = "CUSTOM_ITEM";

const SAFE_SPACE = "__SPACE__";

const isIos = preprocessorQuery.isIos();
const isTablet = preprocessorQuery.isTablet();
const isMobile = preprocessorQuery.isMobile();

class FormColorDropdown extends PureComponent {
  constructor(props, context) {
    super(props, context);
    this.state = {
      showMenu: false,
      options: null,
      showMore: false,
      cursorIndex: null,
      cursorMoreIndex: null
    };
    this._domNode = null;
    this.focus = this.focus.bind(this);
    this.getSelectedOption = this.getSelectedOption.bind(this);
    this.showMenu = this.showMenu.bind(this);
    this.hideMenu = this.hideMenu.bind(this);
    this.showMore = this.showMore.bind(this);
    this.hideMore = this.hideMore.bind(this);
    this.toggleMore = this.toggleMore.bind(this);
    this.onKeyDown = this.onKeyDown.bind(this);
    this.toggle = this.toggle.bind(this);
    this._dropdownId = "x" + uniqueId();
  }

  componentWillReceiveProps(nextProps) {
    if (this.state.showMenu) {
      const options = getDropdownOptions(nextProps.options);
      this.setState({ options });
    }
  }

  getSelectedOption(props) {
    const options = getDropdownOptions(props.options);
    return _.find(options, val => val.value === props.value);
  }

  toggle() {
    if (this.state.showMenu) {
      this.hideMenu();
    } else {
      this.showMenu();
    }
  }

  getMoreColorDomId(color) {
    return `${this._dropdownId}-${color}`;
  }

  getListItemDomId(opt) {
    return `${this._dropdownId}-${opt.value.replace(/\s/g, SAFE_SPACE)}`;
  }

  getCustomListItemDomId() {
    return `${this._dropdownId}-${CUSTOM_ITEM}`;
  }

  onKeyDown(e) {
    const rect = this._domNode.getBoundingClientRect();
    const distFromRight = window.innerWidth - rect.right;
    const popoverOnRight = distFromRight > COLOR_PICKER_POPOVER_WIDTH;

    let keyHandled = false;
    // Is it already open...?
    if (this.state.showMenu) {
      switch (e.key) {
        case KeyConstants.Escape:
          this.hideMenu();
          keyHandled = true;
          break;
        case KeyConstants.Tab:
          this.selectHighlightedOption();
          // TODO: We were not stopping propagation of this... good or bad?
          //keyHandled = true;
          break;
        case KeyConstants.Enter:
          this.selectHighlightedOption();
          keyHandled = true;
          this.focus();
          break;
        case KeyConstants.Up:
          this.moveHighlight(this.state.cursorInMoreList ? -COLORS_PER_ROW : -1);
          keyHandled = true;
          break;
        case KeyConstants.Down:
          this.moveHighlight(this.state.cursorInMoreList ? COLORS_PER_ROW : 1);
          keyHandled = true;
          break;
        case KeyConstants.Left: {
          if (this.state.cursorInMoreList) {
            if (popoverOnRight && this.state.cursorIndex % COLORS_PER_ROW === 0) {
              // Left arrow when we are on the hard-left takes the cursor out of the more list
              this.moveCursorOutOfMoreList();
            } else {
              this.moveHighlight(-1);
            }
          } else if (!popoverOnRight && this.state.cursorIndex === this.state.options.length) {
            this.moveCursorIntoMoreList(popoverOnRight);
          }
          keyHandled = true;
          break;
        }
        case KeyConstants.Right: {
          if (this.state.cursorInMoreList) {
            if (!popoverOnRight && this.state.cursorIndex % COLORS_PER_ROW === COLORS_PER_ROW - 1) {
              // Left arrow when we are on the hard-left takes the cursor out of the more list
              this.moveCursorOutOfMoreList();
            } else {
              this.moveHighlight(1);
            }
          } else if (popoverOnRight && this.state.cursorIndex === this.state.options.length) {
            this.moveCursorIntoMoreList(popoverOnRight);
          }
          keyHandled = true;
          break;
        }
        default:
          break;
      }
    } else {
      // Not currently open.
      // So open it with "enter"
      switch (e.key) {
        case KeyConstants.Enter:
          this.showMenu();
          keyHandled = true;
          break;
        default:
          break;
      }
    }

    if (keyHandled) {
      e.stopPropagation();
      e.preventDefault();
    }
  }

  getHighlightedValue() {
    if (this.state.cursorInMoreList) {
      if (this.state.cursorIndex < 0 || this.state.cursorIndex >= this.props.moreColors.length) {
        return null;
      }
      return this.props.moreColors[this.state.cursorIndex];
    } else {
      if (this.state.cursorIndex < 0 || this.state.cursorIndex >= this.state.options.length) {
        return null;
      }
      return this.state.options[this.state.cursorIndex].value;
    }
  }

  selectHighlightedOption() {
    if (this.state.showMenu) {
      const value = this.getHighlightedValue();
      if (value) {
        this.optionClick(value);
      }
    }
  }

  moveCursorOutOfMoreList() {
    this.setState({
      cursorIndex: this.state.options.length,
      cursorInMoreList: false
    });
  }

  moveCursorIntoMoreList(popoverOnRight) {
    this.setState({
      cursorIndex: popoverOnRight ? 0 : COLORS_PER_ROW - 1,
      cursorInMoreList: true
    });
  }

  scrollToElement(val) {
    let domId = null;
    if (val && val.value && this.state.options.findIndex(o => o.value === val.value) >= 0) {
      domId = this.getListItemDomId(val);
    } else if (val === CUSTOM_ITEM) {
      domId = this.getCustomListItemDomId();
    } else {
      domId = this.getMoreColorDomId(val);
    }
    const domNode = document.getElementById(domId);
    if (domNode) {
      domNode.scrollIntoViewIfNeeded ? domNode.scrollIntoViewIfNeeded() : domNode.scrollIntoView();
    }
  }

  moveHighlight(delta) {
    let cursorIndex = this.state.cursorIndex + delta;
    let showMore = this.state.cursorInMoreList;
    let scrollToValue = null;
    if (cursorIndex < 0) {
      cursorIndex = 0;
    }
    if (this.state.cursorInMoreList) {
      if (cursorIndex >= this.props.moreColors.length) {
        cursorIndex = this.props.moreColors.length - 1;
      }
      scrollToValue = this.props.moreColors[cursorIndex];
    } else {
      if (!this.state.cursorInMoreList && cursorIndex > this.state.options.length) {
        // Note: cursorIndex can equal options length here, as this is the
        cursorIndex = this.state.options.length;
      }
      showMore = cursorIndex === this.state.options.length;
      scrollToValue = showMore ? CUSTOM_ITEM : this.state.options[cursorIndex];
    }
    if (!showMore && this.state.showMore) {
      _.defer(this.focus);
    }
    this.setState({ cursorIndex, showMore });
    this.scrollToElement(scrollToValue);
  }

  showMenu() {
    let cursorInMoreList = false;
    const options = getDropdownOptions(this.props.options);
    let cursorIndex = _.findIndex(options, opt => opt.value === this.props.value);
    let showMore = false;
    if (this.props.moreColors && cursorIndex < 0 && this.props.value) {
      cursorIndex = this.props.moreColors.indexOf(this.props.value);
      cursorInMoreList = cursorIndex >= 0;
      showMore = cursorInMoreList;
    }
    this.setState({ showMenu: true, options, showMore, cursorIndex, cursorInMoreList });
    if (this.props.onToggleMenu) {
      this.props.onToggleMenu(true);
    }
  }

  hideMenu() {
    this.setState({ showMenu: false, showMore: false, options: null });
    if (this.props.onToggleMenu) {
      this.props.onToggleMenu(false);
    }
  }

  showMore() {
    this.setState({ showMore: true });
  }

  hideMore() {
    this.setState({ showMore: false });
  }

  toggleMore() {
    this.setState({ showMore: !this.state.showMore });
  }

  optionClick(value) {
    if (value !== this.props.value) {
      this.props.onChange(value);
    }
    this.hideMenu();
    this.hideMore();
  }

  focus() {
    this._domNode.focus();
    if (this.props.openOnFocus) {
      this.showMenu();
    }
  }

  renderMoreColors() {
    return this.props.moreColors.map((opt, index) => {
      const selected = this.props.value === opt;
      const highlight = this.state.cursorInMoreList && index === this.state.cursorIndex;
      // Note: onMouseDown used instead of onClick, so that it fires before the blur event.
      return (
        <div
          className={`colorPickerOption ${opt} ${selected ? "selected" : ""} ${highlight ? "highlight" : ""}`}
          key={opt}
          id={this.getMoreColorDomId(opt)}
          onMouseDown={() => this.optionClick(opt)}
        />
      );
    });
  }

  renderMorePopover() {
    let placement = "right-start";
    let hideArrow = true;
    let alwaysPortal = false;

    let popoverOptions = {
      className: `modalPopover ${this.props.popoverClassName ? this.props.popoverClassName : ""}`,
      placement,
      hideArrow,
      isOpen: this.state.showMore,
      target: `#${this._dropdownId}`,
      toggle: this.toggleMore,
      boundariesElement: "viewport"
    };

    const child = (
      <Fragment>
        {this.props.screenContext && isScreenModeMobile(this.props.screenContext.screenMode) ? (
          <div onClick={e => e.stopPropagation()}>
            <NavigationToolbar
              title={this.props.title}
              showBack={true}
              showBackArrow={true}
              onBackClick={this.hideMore}
              backText={this.props.backText ? this.props.backText : "Back"}
              flexLeft={1}
              flexCenter={3}
              flexRight={1}
            />
          </div>
        ) : null}
        <PopoverBody onClick={e => e.stopPropagation()}>
          {" "}
          <div>
            <div className="colorPickerMenu">{this.renderMoreColors()}</div>
          </div>
        </PopoverBody>
      </Fragment>
    );

    return popoverOrPortal(child, popoverOptions, alwaysPortal);
  }

  renderMoreListOption() {
    const selected = false;
    const highlighted = !this.state.cursorInMoreList && this.state.cursorIndex === this.state.options.length;
    const optClass = `dropdownMenuOption moreList ${selected ? "selected" : ""} ${highlighted ? "highlighted" : ""}`;

    let mouseOverAction = this.toggleMore;
    let clickAction = null;
    //#!if iOS === true
// mouseOverAction = null;
// clickAction = this.toggleMore;
    //#!endif

    return this.props.moreColors ? (
      <div className={optClass} id={this.getCustomListItemDomId()} onMouseOver={mouseOverAction} onClick={clickAction}>
        <div className="dropdownTick">
          <svg className="icon mono">
            <use xlinkHref={selected ? "#tick" : "#blank"} />
          </svg>
        </div>
        {this.renderOptionColor({ color: "transparent" })}
        <div className="dropdownText">
          <div className="dropdownLabel custom">Custom</div>
        </div>
        <Icon icon="rightArrow" color="iconMono" />
      </div>
    ) : null;
  }

  renderOptionColor(opt) {
    if (opt.color) {
      return <div className={`dropdownColor ${opt.color}`} />;
    } else {
      return null;
    }
  }

  renderMenuOptions() {
    let style = {};
    //#!if mobile === true
// style.paddingLeft = Math.max(this.props.paddingLeft, 5);
// style.paddingRight = Math.max(this.props.paddingRight, 5);
    //#!endif

    return this.state.options.map((opt, index) => {
      const selected = this.props.value === opt.value;
      const highlight = !this.state.cursorInMoreList && this.state.cursorIndex === index;
      const optClass = `dropdownMenuOption ${selected ? "selected" : ""} ${highlight ? "highlighted" : ""}`;
      return (
        <div
          className={optClass}
          style={style}
          key={opt.value}
          id={this.getListItemDomId(opt)}
          onClick={() => this.optionClick(opt.value)}
          onMouseOver={() => this.hideMore()}
        >
          <div className="dropdownTick">
            <svg className="icon mono">
              <use xlinkHref={selected ? "#tick" : "#blank"} />
            </svg>
          </div>
          {this.renderOptionColor(opt)}
          <div className="dropdownText">
            <div className="dropdownLabel">{opt.label}</div>
          </div>
        </div>
      );
    });
  }

  renderMenu() {
    if (this.state.showMenu) {
      // Merge the modal heights
      let style = {};
      if (this.props.alwaysPortal) {
        style.maxHeight = this.props.maxHeight;
        style.height = this.props.fixedHeight;
      }
      style = {
        minWidth: this._domNode && this._domNode.clientWidth ? `${this._domNode.clientWidth}px` : `0`
      };
      return (
        <div style={style} className="dropdownMenu noSearch">
          <div className="dropdownMenuOptions">
            {this.renderMenuOptions()}
            {this.renderMoreListOption()}
          </div>
          {this.state.showMore ? this.renderMorePopover() : null}
        </div>
      );
    }
    return null;
  }

  renderPopover() {
    let placement = "bottom-start";
    let hideArrow = true;
    let alwaysPortal = false;

    if (isTablet) {
      placement = "bottom-end";
      hideArrow = false;
    }

    let popoverOptions = {
      className: `dropdownPopover${isIos ? " iosModalContentWrap" : ""} ${
        this.props.popoverClassName ? this.props.popoverClassName : ""
      }`,
      placement,
      hideArrow,
      isOpen: this.state.showMenu,
      target: this._dropdownId,
      toggle: this.hideMenu
    };

    const child = (
      <Fragment>
        {this.props.screenContext && isScreenModeMobile(this.props.screenContext.screenMode) ? (
          <NavigationToolbar
            title={this.props.title}
            showBack={true}
            showBackArrow={true}
            onBackClick={this.toggle}
            backText={this.props.backText ? this.props.backText : "Back"}
          />
        ) : null}
        <PopoverBody onClick={e => e.stopPropagation()}>{this.renderMenu()}</PopoverBody>
      </Fragment>
    );

    return popoverOrPortal(child, popoverOptions, alwaysPortal);
  }

  render() {
    let arrowIcon = isMobile ? "rightArrow" : "downArrow";

    let selectedOption = this.getSelectedOption(this.props);
    if (!selectedOption && this.props.value) {
      selectedOption = {
        color: this.props.value,
        label: "Custom",
        custom: true
      };
    }
    return (
      <div className="formDropdown">
        <div
          ref={node => (this._domNode = node)}
          className={`dropdown ${this.props.disabled ? "disabled" : ""}`}
          tabIndex={this.props.disabled ? -1 : this.props.tabIndex ? this.props.tabIndex : 0}
          onBlur={this.onBlur}
          onKeyDown={this.onKeyDown}
          id={this._dropdownId}
        >
          <div
            className={`dropdownControl ${this.state.showMenu && !this.props.disabled ? "open" : "closed"}`}
            onClick={this.props.disabled ? null : this.toggle}
          >
            {selectedOption ? this.renderOptionColor(selectedOption) : null}
            <div className="dropdownText">
              <div
                className={`dropdownLabel ${this.props.centerLabel ? "centered" : ""} ${
                  selectedOption && selectedOption.custom ? "custom" : ""
                }`}
              >
                {selectedOption ? selectedOption.label : "None"}
              </div>
            </div>
            <Icon className="downArrow" icon={arrowIcon} color="mono" />
          </div>
          {this.state.showMenu && !this.props.disabled ? this.renderPopover() : null}
        </div>
      </div>
    );
  }
}

FormColorDropdown.propTypes = {
  options: PropTypes.oneOfType([PropTypeShapes.listOptionArray, PropTypes.func]).isRequired,
  moreColors: PropTypes.arrayOf(PropTypes.string),
  openOnFocus: PropTypes.bool,
  value: PropTypes.string,
  centerLabel: PropTypes.bool,
  disabled: PropTypes.bool,
  tabIndex: PropTypes.number,
  onChange: PropTypes.func,
  popoverClassName: PropTypes.string,
  onToggleMenu: PropTypes.func,
  title: PropTypes.string,
  backText: PropTypes.string,

  //#!if iOS === true
// alwaysPortal: PropTypes.bool,
// 
  // From withScreenContext
// screenContext: PropTypes.object,
// 
  // From ModalSizeContext
// maxHeight: PropTypes.number,
// fixedHeight: PropTypes.number,
// 
  // From withContainerContext
// paddingLeft: PropTypes.number,
// paddingRight: PropTypes.number
  //#!endif
};

let exportedView = FormColorDropdown;
//#!if iOS === true
// exportedView = withContainerContext(withScreenContext(withModalSizeContext(FormColorDropdown)));
//#!endif
export default exportedView;
